import React, { useState } from "react";
import { Popover } from "antd";
import { SmileOutlined } from "@ant-design/icons";
import Picker, { EmojiClickData } from "emoji-picker-react";

interface EmojiPickerProps {
  onTextChange?: (newText: string) => void;
  currentText?: string;
  placement?: "top" | "left" | "right" | "bottom";
  targetInputRef?: React.RefObject<HTMLInputElement | HTMLTextAreaElement>;
}

const EmojiPicker: React.FC<EmojiPickerProps> = ({
  onTextChange,
  placement = "right",
  targetInputRef,
  currentText
}) => {
  const [visible, setVisible] = useState(false);
  const [cursorPos, setCursorPos] = useState<number | null>(null);

  // ✅ Reusable helper to get native input/textarea element
  const getNativeInput = () => {
    const wrapper = targetInputRef?.current;
    return wrapper?.['input'] || wrapper?.['resizableTextArea']?.['textArea'] || wrapper;
  };

  const updateCursorPosition = () => {
    const input = getNativeInput();
    if (input && typeof input.selectionStart === "number") {
      setCursorPos(input.selectionStart);
    }
  };
//emoji categories to show
  const categories = [
    { category: "smileys_people" },
    { category: "animals_nature" },
    { category: "food_drink" },
    { category: "travel_places" },
    { category: "activities" },
    { category: "objects" },
    { category: "symbols" },
  ];
//insert emoji are corect position
  const insertEmojiAtCursor = (
    text: string,
    emoji: string,
    position: number
  ) => {
    return text.slice(0, position) + emoji + text.slice(position);
  };

  const handleEmojiClick = (emojiData: EmojiClickData) => {
    const emoji = emojiData?.emoji;
    if (!emoji) return;

    const input = getNativeInput();
    if (!input) return;

    input.focus();

    const position = cursorPos ?? input.value.length;
    const originalText = currentText || "";
    const updatedText = insertEmojiAtCursor(originalText, emoji, position);

    if (onTextChange) {
      onTextChange(updatedText);
    }

    requestAnimationFrame(() => {
      const newCursorPosition = position + emoji.length;
      input.setSelectionRange(newCursorPosition, newCursorPosition);
      setCursorPos(newCursorPosition);
      input.blur(); 
    });

  };

  const handleIconClick = () => {
    const input = getNativeInput();
    if (input) {
      input.focus();
    }
    updateCursorPosition();
    setVisible(true);
  };

  return (
    <Popover
      trigger="click"
      content={
        <div onClick={(e) => e.stopPropagation()}>
          <Picker
            categories={categories}
            onEmojiClick={handleEmojiClick}
            lazyLoadEmojis
            skinTonesDisabled
            previewConfig={{ showPreview: false }}
            emojiStyle="native" // uses system font emojis
          />
        </div>
      }
      open={visible}
      onOpenChange={setVisible}
      placement={placement}
      destroyTooltipOnHide
      overlayInnerStyle={{ transform: 'translateY(-50%)' }} // center vertically

    >
      <span>
        <SmileOutlined
          onClick={handleIconClick}
          style={{ cursor: "pointer" }}
        />
      </span>
    </Popover>
  );
};

export default EmojiPicker;

